import { Box } from '@mui/material'
import { CargoType, CarrierType } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { InitializationWrapper } from '@planning/components/InitializationWrapper'
import { VesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { orderListParsingService } from '@planning/services'
import { useMinimalsTheme } from '@tom-ui/ui'
import { downloadFile, PATHS, useAsyncFetch, withAuthenticationRequired } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import Papa from 'papaparse'
import { ReactNode, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { vesselVisitDetailPages } from './Components/CarrierVisitDashboard'
import { VesselVisitDashboard, VesselVisitDashboardExternalDeps } from './VesselVisitDashboard'

export enum VesselVisitDirection {
  Inbound = 'Discharge',
  Outbound = 'Load',
}

export const CarrierVisitDirection = {
  Inbound: 'Inbound',
  Outbound: 'Outbound',
} as const

export type CarrierVisitDirection =
  (typeof CarrierVisitDirection)[keyof typeof CarrierVisitDirection]

export interface VesselVisitDetailsExternalDeps extends VesselVisitDashboardExternalDeps {
  /** Called once per load to pre-fetch equipment plan data */
  onFetchEquipments?: (vesselVisitId: number) => Promise<void>
  /** Called once per load to pre-fetch issue data */
  onFetchIssues?: (vesselVisitId: number) => Promise<void>
  /** Fetches damage amounts for general cargo orders */
  getOrderDamageAmounts?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; totalQuantity: number }>>
  /** Container yard operation dialog rendered by host */
  containerOperationDialog?: ReactNode
}

export const VesselVisitDetails = withAuthenticationRequired(
  observer(
    ({
      onFetchEquipments,
      onFetchIssues,
      getOrderDamageAmounts,
      containerOperationDialog,
      ...dashboardDeps
    }: VesselVisitDetailsExternalDeps) => {
      const navigate = useNavigate()
      const theme = useMinimalsTheme()

      const {
        vesselVisitItemStore,
        vesselVisitQueryService,
        generalCargoViewStore,
        restowItemStore,
        orderItemStore,
        containerItemStore,
      } = usePlanningStore()

      const { id } = useParams()
      const vesselVisitId = Number(id)
      if (!vesselVisitId) {
        navigate(PATHS.visits.vesselVisits.url)
      }

      const vesselVisitItem = computed(
        () => vesselVisitItemStore.elements[vesselVisitId] as VesselVisitItem,
      ).get()

      const [operationsInitialized, setOperationsInitialized] = useState(false)
      useAsyncFetch(async () => {
        await vesselVisitQueryService.fetchById(vesselVisitId)
        await orderItemStore.fetchByVisitId(vesselVisitId)
        await containerItemStore.fetchByVisitId(vesselVisitId)
        if (!operationsInitialized) {
          await onFetchEquipments?.(vesselVisitId)
          await onFetchIssues?.(vesselVisitId)
          setOperationsInitialized(true)
        }
      }, [
        vesselVisitId,
        vesselVisitQueryService,
        orderItemStore,
        containerItemStore,
        onFetchEquipments,
        onFetchIssues,
      ])

      const orders = computed(() =>
        vesselVisitItemStore.ordersByVesselVisitId
          ? vesselVisitItemStore.ordersByVesselVisitId[vesselVisitId]
          : [],
      ).get()

      useEffect(() => {
        const fetchDamages = async () => {
          if (
            vesselVisitItem &&
            vesselVisitItem.data.cargoType === CargoType.GeneralCargo &&
            orders?.length
          ) {
            if (getOrderDamageAmounts) {
              const result = await getOrderDamageAmounts(orders.map(x => x.id))
              generalCargoViewStore.setDamageOrders(
                result.map(x => ({ amount: x.totalQuantity, orderId: x.orderId })),
              )
            }
          }
        }
        fetchDamages()
      }, [generalCargoViewStore, orders, vesselVisitItem, getOrderDamageAmounts])

      useEffect(() => {
        restowItemStore.fetchByVesselVisitId(vesselVisitId)
      }, [restowItemStore, vesselVisitId, vesselVisitItem])

      const handlePageChange = (page: string) => {
        if (page === `${PATHS.issues.url}`) {
          navigate(`${PATHS.issues.url}`, {
            state: { visitId: vesselVisitItem.id },
          })
        } else navigate(`${PATHS.visits.vesselVisits.url}/${vesselVisitItem.id}/${page}`)
      }

      const handleExportOrders = (currentPage: string) => {
        const direction =
          currentPage === vesselVisitDetailPages.discharge
            ? CarrierVisitDirection.Inbound
            : CarrierVisitDirection.Outbound

        const { fileName, header, data } = orderListParsingService.parseOrderListExport(
          CarrierType.Vessel,
          direction,
          vesselVisitItem,
        )

        const csvData = Papa.unparse({
          fields: header,
          data: data,
        })

        downloadFile(new Blob([csvData as BlobPart]), fileName)
      }

      return (
        <InitializationWrapper isInitializing={!vesselVisitItem}>
          <Box sx={{ backgroundColor: theme.palette.grey[200], minHeight: '100vh' }}>
            <VesselVisitDashboard
              vesselVisitItem={vesselVisitItem}
              handlePageChange={handlePageChange}
              visitId={vesselVisitId}
              onClickExportOrders={direction => {
                const operation = direction === CarrierVisitDirection.Inbound ? 'discharge' : 'load'
                handleExportOrders(operation)
              }}
              {...dashboardDeps}
            />
            {containerOperationDialog}
          </Box>
        </InitializationWrapper>
      )
    },
  ),
)
